import { AfterViewChecked, Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CommonService } from '../../services/common.service';
import { LocalStorageService } from '../../services/local-storage.service';

import { AllDropdownKeys, ComponentRoutes, UrlSegments } from '../jurisdiction.constants';
import { TechnicianDataService } from '../technician-data/technician-data.service';
import { GetGridConnectionObject, GridConnectionData, SpecialConsumerDeviceTypeChange } from '../technician-data/technician-data.domain';
import { ErrorService } from '../../services/error/error.service';
import { TechnicianFormType } from '../../constants/app.constants';
import { FacilitiesFormService } from '../facilities-form/facilities-form.service';
import { GetInstallationApplicationObject } from '../facilities-form/facilities-form.domain';
import { FormTypesData } from '../../domains/common.domain';
import { FileService } from '../../services/file.service';
import { HttpResponse } from '@angular/common/http';
import { CommunicationService } from '../../services/communication.service';
import { forkJoin, from, of } from 'rxjs';
import { TechnicianResponseData } from '../../employee-overview/employee-overview.domain';
import { EnvironmentService } from '../../services/environment.service';
import { NetzTranslateService } from '../../services/netz-translate.service';
import { PersonalDataComponent } from '../personal-data/personal-data.component';
import { FacilitiesDataComponent } from '../facilities-data/facilities-data.component';
import { TechnicalDataComponent } from '../technical-data/technical-data.component';
import { PrintService } from 'src/app/+grid-application-shared/services/print.service';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { concatMap } from 'rxjs/operators';
import { ReviewSectionService } from 'src/app/+shared/components/review-section/review-section.service';
import { ReviewSectionStore } from 'src/app/+shared/components/review-section/review-section.store';
import { TechnicianDataComponent } from '../technician-data/technician-data.component';
import { ContactPersonComponent } from 'src/app/+shared/components/contact-person/contact-person.component';
import { UserService } from 'src/app/services/user.service';
import { SectionIdentifier, SectionReview, SectionStatus } from 'src/app/+shared/components/review-section/review-section.domain';
import { DocumentsSubmissionComponent } from 'src/app/+shared/components/documents-submission/documents-submission.component';
import { UpdateInstallerData } from '../jurisdiction.domain';
import { PersonalDataService } from '../personal-data/personal-data.service';
import { SiteElectricityInstallationTechnicalDataComponent } from '../siteelectricity-installation-technical-data/siteelectricity-installation-technical-data.component';

@Component({
  providers: [TechnicianDataService],
  selector: 'app-process-details',
  styleUrls: ['./process-details.component.scss'],
  templateUrl: './process-details.component.html',
})
export class ProcessDetailsComponent extends DestroyableComponent implements OnInit, AfterViewChecked {
  @ViewChild(PersonalDataComponent) personalData: PersonalDataComponent;
  @ViewChildren(FacilitiesDataComponent) facilitiesData: QueryList<FacilitiesDataComponent>;
  @ViewChild(TechnicalDataComponent) technicalData: TechnicalDataComponent;
  @ViewChild(SiteElectricityInstallationTechnicalDataComponent) siteElectricityData: SiteElectricityInstallationTechnicalDataComponent;
  @ViewChild(TechnicianDataComponent) technicianData: TechnicianDataComponent;
  @ViewChild(ContactPersonComponent) contactPersonComponent: ContactPersonComponent;
  @ViewChild(DocumentsSubmissionComponent) documentsComponent: DocumentsSubmissionComponent;

  applicationId: string;
  facilityTypes: FormTypesData[];
  installationType: string;
  isProcessDetails: boolean;
  isLoading: boolean;
  isReadOnly: boolean;
  loadGridConnectionComponents: boolean;
  loadInstallationApplicationComponents: boolean;
  showSendMessageButton: boolean;
  showTechnicalFormFirst: boolean;
  showOrderProcessChangeBtns: boolean;
  showRequestChangeBtn: boolean;
  showUpdateApplicationBtn: boolean;
  executorAction: string;
  role: string;
  powerConnectionFormValid: boolean = false;

  isApplicationOwner: boolean;
  contactPersonData: any;

  private applicationType: string;
  private isBusinessPartner: boolean;
  private isNewMainsPowerConnection: boolean;

  // workaround for draft rejected application
  get isDetailsView(): boolean {
    return this.isCustomer && this.isDraftRejectedRequest ? false : this.isProcessDetails;
  }

  get dataIsEditable(): boolean {
    return (
      this.showRequestChangeBtn && this.isApplicationOwner && (this.executorAction === 'update_application' || this.isDraftRejectedRequest)
    );
  }

  get isTechnician(): boolean {
    return this.userService.isUserTechnicianRole();
  }

  get isCustomer(): boolean {
    return this.userService.isUserCustomerRole();
  }

  get isDraftRequest(): boolean {
    return this.technicianData?.applicationStatus == 'draft';
  }

  get isDraftRejectedRequest(): boolean {
    return this.technicianData?.applicationStatus == 'draftRejected';
  }

  get showApproveAndRejectButtons(): boolean {
    const isMainsConnection = this.applicationType === 'connection';
    const showTempRegistration = this.environmentService.showTempConnectionRegistrationForNewMains;
    const requiredTempConnectionLess30 = this.technicianData?.powerConnectionData?.requiredTempConnectionLess30 ?? false;

    return (
      this.isTechnician &&
      isMainsConnection &&
      showTempRegistration &&
      requiredTempConnectionLess30 &&
      (this.isDraftRequest || this.isDraftRejectedRequest)
    );
  }

  get showUpdateAndWithdrawButton(): boolean {
    const isMainsConnection = this.applicationType === 'connection';
    const showTempRegistration = this.environmentService.showTempConnectionRegistrationForNewMains;
    const requiredTempConnectionLess30 = this.technicianData?.powerConnectionData?.requiredTempConnectionLess30 ?? false;

    return this.isCustomer && isMainsConnection && showTempRegistration && requiredTempConnectionLess30 && this.isDraftRejectedRequest;
  }

  get isTempPowerReportConfirmed(): boolean {
    const powerConnection = this.localStorageService.getItem('power_connection');
    return powerConnection?.tempPowerReportConfirmed;
  }

  get loadOrderProcess(): boolean {
    return (
      this.isApplicationOwner || this.userService.isChimneySweeper() || this.isBusinessPartner || this.userService.isUserModeratorRole()
    );
  }

  get useOrderHistoryPagination(): boolean {
    return this.environmentService.useOrderHistoryPagination && this.applicationType === 'connection' && this.isNewMainsPowerConnection;
  }

  get isTechnicalDataValid(): boolean {
    if (this.facilitiesData?.some(facility => facility.disableSubmitBtn())) {
      return false;
    }
    if (this.technicalData?.technicalDataFG && !this.technicalData.technicalDataFG.valid) {
      return false;
    }
    if (this.siteElectricityData?.installationTechnicalDataFG && !this.siteElectricityData.installationTechnicalDataFG.valid) {
      return false;
    }
    return true;
  }

  constructor(
    public commonService: CommonService,
    private errorService: ErrorService,
    private facilitiesFormService: FacilitiesFormService,
    private fileService: FileService,
    private localStorageService: LocalStorageService,
    private technicianDataService: TechnicianDataService,
    private route: ActivatedRoute,
    private router: Router,
    private communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private netzTranslateService: NetzTranslateService,
    private printService: PrintService,
    private reviewSectionService: ReviewSectionService,
    private reviewSectionStore: ReviewSectionStore,
    private userService: UserService,
    private personalDataService: PersonalDataService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.applicationType = this.localStorageService.getItem('application_type');
    this.role = this.localStorageService.getItem('role');
    this.loadGridConnectionComponents = false;
    this.loadInstallationApplicationComponents = false;
    this.installationType = '';
    this.facilityTypes = this.localStorageService.getItem('selected_facilities') || [];
    this.isReadOnly = this.route.snapshot.queryParamMap.get('isReadOnly') === 'true';

    this.showTechnicalFormFirst = this.environmentService.showTechnicalFormFirst;
    this.showOrderProcessChangeBtns = this.environmentService.showOrderProcessChangeBtns;
    this.showRequestChangeBtn = this.environmentService.showRequestChangeBtn;

    if (this.applicationType) {
      this.loadApplicationData();
    }

    this.preventLeak(this.route.url).subscribe(urlSegments => {
      this.isProcessDetails = urlSegments[0].path === ComponentRoutes.ProcessDetails;
    });
  }

  ngAfterViewChecked() {
    // Track form validity changes
    this.technicianData?.powerConnectionForm?.statusChanges?.subscribe(status => {
      this.powerConnectionFormValid = status === 'VALID';
    });
  }

  sendMessage(): void {
    const applicationTypes = this.commonService.getDropdownData(AllDropdownKeys.ApplicationType);
    let technicianId: string;
    let applicationData = null;

    if (this.applicationType === applicationTypes[1].value) {
      // connection-flow
      technicianId = this.commonService.gridConnectionApplicationData.user_id;
      applicationData = { ...this.commonService.gridConnectionApplicationData };
    } else if (this.applicationType === applicationTypes[0].value) {
      // installation-flow
      technicianId = this.commonService.installationApplicationData.technician_id;
      applicationData = { ...this.commonService.installationApplicationData };
    }

    const dataObj = {
      applicationType: this.localStorageService.getItem('application_type_detail'),
      externalId: applicationData.application_ext_id,
      connectionLocation: `${applicationData.connectionobject.postcode} ${applicationData.connectionobject.city}, ${applicationData.connectionobject.street} ${applicationData.connectionobject.street_no}`,
      businessPartner: `${applicationData.businesspartner.lastname}, ${applicationData.businesspartner.firstname}`,
    };
    const subjectArray = [`${this.netzTranslateService.translateString('messages.subject-text')}: ${dataObj.externalId}`];
    subjectArray.push(
      this.netzTranslateService.translateString(`lookup.sk_application_type_detail.options.${dataObj.applicationType}.name`)
    );
    subjectArray.push(...[dataObj.connectionLocation, dataObj.businessPartner]);
    const messageData = {
      subject: subjectArray.join(' | '),
      firstName: applicationData.businesspartner.firstname,
      lastName: applicationData.businesspartner.lastname,
    };
    this.localStorageService.setItem('message_data', messageData);

    this.router.navigate(['/jurisdiction-overview/messages', technicianId]);
  }

  print(): void {
    window.print();
  }

  downloadAllDocuments(): void {
    this.isLoading = true;

    if (this.environmentService.useSeparatedPdfDownload) {
      const facilityKeys = (this.facilityTypes || []).map(f => f.key);

      this.preventLeak(this.printService.generateSeparatedPdfsByCurrentPage())
        .pipe(
          concatMap((blobs: Blob[]) => {
            const fileNames = blobs.map((_, i) => `printedForm_${facilityKeys[i] || i}`);
            return this.fileService.getAllDocumentsByApplicationIdMultiple(this.applicationId, blobs, 'other', fileNames);
          })
        )
        .subscribe(
          (response: HttpResponse<Blob>) => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(response, false);
            this.fileService.downloadZipDocument(response);
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
    } else {
      this.preventLeak(this.printService.generatePdfByCurrentPage())
        .pipe(concatMap((blob: Blob) => this.fileService.getAllDocumentsByApplicationId(this.applicationId, blob, 'other', 'printedForm')))
        .subscribe(
          (response: HttpResponse<Blob>) => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(response, false);
            this.fileService.downloadZipDocument(response);
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
    }
  }

  saveApplicationData(): () => Promise<void> {
    return async (): Promise<void> => {
      try {
        // Thunks: each save starts only after the previous one resolved. The documents/status
        // submission goes last so the status change never races the section saves.
        const steps: Array<() => Promise<void>> = [];

        if (this.technicalData) {
          steps.push(() => this.technicalData.saveFormData());
        }
        if (this.siteElectricityData) {
          steps.push(() => this.siteElectricityData.saveFormData());
        }
        this.facilitiesData?.forEach(facility => steps.push(() => facility.submitForm()));
        if (this.technicianData) {
          steps.push(() => this.technicianData.submitForm());
          steps.push(async () => this.documentsComponent?.linkNewSpecialConsumers(this.technicianData.getSpecialConsumerIdentities()));
        }
        if (this.contactPersonComponent) {
          steps.push(() => this.contactPersonComponent.saveChanges());
        }
        steps.push(() => this.personalData.saveFormData());
        if (this.documentsComponent) {
          steps.push(() => this.documentsComponent.submitFormData(false));
        }

        for (const step of steps) {
          await step();
        }
      } catch (error) {
        return Promise.reject(error);
      }

      setTimeout(() => {
        this.errorService.createMultipleMessages([this.netzTranslateService.translateString('submitted.new_changes')], 'success');
      }, 1500);

      return Promise.resolve();
    };
  }

  setExecutorAction(executorAction: string): void {
    this.executorAction = executorAction;
  }

  approveApplication() {
    this.communicationService
      .updateData(`${UrlSegments.ApproveGridConnectionApplication}${this.applicationId}`, {
        sk_status: 'submitted',
      })
      .subscribe(() => {
        this.router.navigate(['/jurisdiction-overview']);
      });
  }

  rejectApplication() {
    this.communicationService
      .updateData(`${UrlSegments.RejectGridConnectionApplication}${this.applicationId}`, {
        sk_status: 'draftRejected',
      })
      .subscribe(() => {
        this.router.navigate(['/jurisdiction-overview']);
      });
  }

  updateTechnicianData() {
    // Prepare requestData BEFORE starting any requests
    const requestData: UpdateInstallerData = this.personalDataService.prepareInstallerData(
      this.personalData.personalDataFormValues.installerData?.value
    );

    const powerConnection = this.localStorageService.getItem('power_connection');
    const skStatus = powerConnection?.requiredTempConnectionLess30 && powerConnection?.technicianBdewId ? 'draft' : 'submitted';
    let sequence$ = this.communicationService
      .updateData(`${UrlSegments.GridConnectionApplication}${this.applicationId}`, { sk_status: skStatus })
      .pipe(
        // After first completes, execute submitForm
        concatMap(() => {
          const submitFormPromise = this.technicianData.submitForm();
          return submitFormPromise instanceof Promise ? from(submitFormPromise) : of(submitFormPromise);
        }),
        // After submitForm completes, conditionally execute saveInstallerData
        concatMap(() => {
          if (
            requestData.powergridcon &&
            this.personalData.personalDataFormValues.installerData.dirty &&
            this.technicianData?.powerConnectionData?.id
          ) {
            const requestUrl = `${UrlSegments.UpdatePowerGridConnection}${this.technicianData.powerConnectionData.id}`;
            return this.personalDataService.saveInstallerData(requestUrl, requestData.powergridcon);
          }

          // If condition not met, return empty observable to complete the sequence
          return of(null);
        })
      );

    sequence$.subscribe(() => {
      this.router.navigate(['/jurisdiction-overview']);
    });
  }

  withdrawApplication() {
    this.communicationService
      .updateData(`${UrlSegments.GridConnectionApplication}${this.applicationId}`, {
        sk_status: 'refused',
      })
      .subscribe(() => {
        this.router.navigate(['/jurisdiction-overview']);
      });
  }

  tempConnectionConfirmed(value: boolean): void {
    this.personalData?.loadResponsibleInstallerData(value);
  }

  powerConnectionReviewSet(review: SectionReview) {
    if (
      this.environmentService.usePowerConnectionExtendedOptionsForNewMains &&
      review.section == SectionIdentifier.TechnicalData_BaseData_Power &&
      review.status == SectionStatus.rejected
    ) {
      this.documentsComponent?.documentsSection?.createSectionReview(SectionStatus.rejected);
    }
  }

  powerConnectionLocationChange(installationLocationType: string = undefined) {
    this.documentsComponent.rebuildComponent(installationLocationType);
  }

  onSpecialConsumerDeviceTypeChange(change: SpecialConsumerDeviceTypeChange): void {
    this.documentsComponent?.syncSpecialConsumerDocument(change, this.technicianData?.getSpecialConsumerIdentities());
  }

  private loadApplicationData(): void {
    this.getApplicationData(() => {
      if (!this.loadOrderProcess) return;

      this.preventLeak(this.reviewSectionService.getSectionReviews(this.applicationId)).subscribe(sectionReviews => {
        this.reviewSectionStore.setReviews(sectionReviews);
      });

      this.preventLeak(this.reviewSectionService.getSectionMessages(this.applicationId)).subscribe(sectionReviews => {
        this.reviewSectionStore.setSectionMessages(sectionReviews);
      });
    });
  }

  private getApplicationData(onSuccess: any): void {
    const applicationTypes = this.commonService.getDropdownData(AllDropdownKeys.ApplicationType);
    if (this.applicationType === applicationTypes[1].value) {
      // connection-flow
      this.getConnectionAppData(onSuccess);
    } else if (this.applicationType === applicationTypes[0].value) {
      // installation-flow
      this.isLoading = true;
      this.getInstallationAppData(onSuccess);
    }
  }

  private getInstallationAppData(onSuccess: any) {
    this.facilitiesFormService.getInstallationApplication().subscribe(
      (response: GetInstallationApplicationObject) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.installationApplicationData = response.installationapplication[0];
        this.installationType = response.installationapplication[0].sk_installation_type;
        this.applicationId = response.installationapplication[0].application_id;
        this.loadInstallationApplicationComponents = true;
        this.isApplicationOwner =
          this.commonService.installationApplicationData.user_id === this.localStorageService.getItem('technician_id');
        this.contactPersonData = this.commonService.installationApplicationData.contact_person;
        this.setMessageButtonVisibility();
        if (onSuccess) onSuccess();
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private getConnectionAppData(onSuccess: any): void {
    const selectedTechnicianId = this.localStorageService.getItem('selected_technician_id');
    const applicationDataSubscription = this.technicianDataService.getGridConnectionApplication();
    const technicianDataSubscription = this.communicationService.getData(`technician?technician_id=${selectedTechnicianId}`);
    this.isLoading = true;
    forkJoin([applicationDataSubscription, technicianDataSubscription]).subscribe(
      (response: [GetGridConnectionObject, TechnicianResponseData]) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        if (response && response.length === 2) {
          this.commonService.gridConnectionApplicationData = response[0].gridconapplication[0];
          this.applicationId = this.commonService.gridConnectionApplicationData.application_id;
          this.isApplicationOwner =
            this.commonService.gridConnectionApplicationData.user_id === this.localStorageService.getItem('technician_id');
          this.contactPersonData = this.commonService.gridConnectionApplicationData.contact_person;
          this.isBusinessPartner =
            this.commonService.gridConnectionApplicationData.businesspartner?.email?.toLocaleLowerCase() ===
            this.localStorageService.getItem('user_email').toLocaleLowerCase();
          this.isNewMainsPowerConnection =
            this.commonService.gridConnectionApplicationData.sk_gridconapplication_type === 'new' &&
            this.commonService.gridConnectionApplicationData.change_power === '1';
          this.parseGridConnectionResponse(this.commonService.gridConnectionApplicationData);
          const technicianData = (response[1].technician && response[1].technician[0]) || null;
          if (technicianData) {
            this.commonService.getPermittedTradesData(technicianData);
          }
        }

        if (onSuccess) onSuccess();
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private parseGridConnectionResponse(response: GridConnectionData): void {
    const selectedBuildingType = TechnicianFormType.filter(item => item.lookUpKey === response.sk_building_type);
    this.localStorageService.setItem('selected_building_type', selectedBuildingType);
    this.localStorageService.setItem('selected_change_type', response.sk_change_subtype);
    this.loadGridConnectionComponents = true;
    this.setMessageButtonVisibility();
  }

  private setMessageButtonVisibility(): void {
    this.showSendMessageButton =
      ['administrator', 'employee', 'observer'].indexOf(this.role) > -1 &&
      (this.loadInstallationApplicationComponents || this.loadGridConnectionComponents);
  }
}
